<template>
  <table class="w-full border-collapse">
    <thead class="bg-background sticky top-0">
      <tr>
        <th
          class="text-background-fg/70 border-b-1 border-background-fg/8 whitespace-nowrap p-2 text-left text-sm font-normal"
        >
        </th>
        <template
          v-for="column in columns"
          :key="column.id || column.header"
        >
          <th
            class="text-background-fg/70 border-b-1 border-background-fg/8 whitespace-nowrap p-2 text-left text-sm font-normal"
          >
            {{ column.header || "" }}
          </th>
        </template>
        <th />
      </tr>
    </thead>
    <tbody>
      <template
        v-for="row in computedRows"
        :key="row.index"
      >
        <tr
          :class="[
            {
              update: 'bg-info/10',
              new: 'bg-positive/10',
              delete: 'bg-error/10',
              unchanged: '',
            }[row.status],
            '',
          ]"
        >
          <td class="w-1 p-2 text-center">
            <span
              :class="[
                {
                  update: 'bg-info',
                  new: 'bg-positive',
                  delete: 'bg-error',
                  unchanged: 'bg-background-fg/30',
                }[row.status],
                'rounded-full px-3 py-1 text-sm text-white',
                '',
              ]"
            >
              {{ row.status }}
            </span>
          </td>
          <template
            v-for="column in columns"
            :key="column.id || column.header"
          >
            <td
              :class="[
                `p-2 font-medium`,
                column.autoWidth
                  ? 'w-1 whitespace-nowrap'
                  : '',
              ]"
            >
              <template v-if="column.id === 'self_color'">
                <ColorItem
                  :value="
                    row.fields[column.id].isChanged
                      ? (row.fields[column.id].newValue as
                          | string
                          | undefined)
                      : (row.fields[column.id].oldValue as
                          | string
                          | undefined)
                  "
                  rounded
                />
              </template>
              <template v-else>
                <div
                  class="flex h-12 flex-col items-start justify-center space-y-1"
                >
                  <template
                    v-if="row.fields[column.id].isChanged"
                  >
                    <span
                      :class="[
                        {
                          update: 'bg-info/20',
                          new: 'bg-positive/20',
                          delete: 'bg-error/20',
                          unchanged: '',
                        }[row.status],
                        'rounded px-1',
                      ]"
                    >
                      {{ row.fields[column.id].newValue }}
                    </span>
                    <span
                      :class="['px-1 text-xs line-through']"
                    >
                      {{ row.fields[column.id].oldValue }}
                    </span>
                  </template>
                  <template v-else>
                    <span :class="['rounded px-1']">
                      {{ row.fields[column.id].oldValue }}
                    </span>
                  </template>
                </div>
              </template>
            </td>
          </template>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<script setup lang="ts">
import type { ImportRow } from "@/services/imports/import"
import type { ImportProduct } from "@/services/imports/retrieveImport"

type ImportColumnDefinition = Array<{
  id: string
  header?: string
  autoWidth?: boolean
  getValue:
    | keyof ImportProduct
    | ((row: Partial<ImportProduct>) => unknown)
}>

const props = withDefaults(
  defineProps<{
    rows?: Array<ImportRow<ImportProduct>>
  }>(),
  { rows: () => [] }
)

const { formatToMerchantCurrencyString } = useCurrency()

const columns: ImportColumnDefinition = [
  {
    id: "name",
    header: "Name",
    getValue: "name",
  },
  {
    id: "parentCategory",
    header: "Category",
    autoWidth: true,
    getValue: "parentCategory",
  },

  {
    id: "price",
    header: "Price",
    autoWidth: true,
    getValue: (instance) => {
      if (!instance) return
      if (
        instance.hasVariablePrice === undefined &&
        instance.price === undefined
      )
        return undefined

      return instance.hasVariablePrice
        ? "variable"
        : instance.price === undefined
          ? undefined
          : formatToMerchantCurrencyString(instance.price)
    },
  },
  {
    id: "vat_rate",
    header: "VAT",
    autoWidth: true,
    getValue: (instance) => {
      if (!instance) return
      return instance.vatRate === undefined
        ? undefined
        : instance.vatRate !== null
          ? formatToPercentage(instance.vatRate / 100)
          : "None"
    },
  },
  {
    id: "self_color",
    header: "Color",
    autoWidth: true,
    getValue: "selfColor",
  },
]

const computedRows = computed(() =>
  props.rows.map((row) => ({
    ...row,
    fields: parseFields(row),
  }))
)

const parseFields = (row: ImportRow<ImportProduct>) => {
  return columns.reduce(
    (accumulator, column) => {
      const oldValue =
        typeof column.getValue === "string"
          ? row.oldInstance?.[column.getValue]
          : row.oldInstance == null
            ? row.oldInstance
            : column.getValue(row.oldInstance)

      const newValue = row.patch
        ? typeof column.getValue === "string"
          ? row.patch[column.getValue]
          : column.getValue(row.patch)
        : oldValue

      const isChanged =
        newValue !== undefined && oldValue !== newValue

      accumulator[column.id] = {
        isChanged,
        oldValue,
        newValue,
      }
      return accumulator
    },
    {} as Record<
      string,
      { isChanged: boolean; oldValue: any; newValue: any }
    >
  )
}
</script>
